import { Header } from "components/common/header/Header";
import "./SignIn.css";
import { Input } from "components/common/input/Input";
import { Button } from "wiz-front-shared/components/button/Button";
import { CheckBox } from "wiz-front-shared/components/checkBox/CheckBox";
import { useQuery } from "hooks/useQuery";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { userStore } from "store/userStore";
import { useUser } from "hooks/useUser";
import { useAtomValue, useSetAtom } from "jotai";
import { resetSignUpData } from "store/signUpStore";
import Svgs from "assets/svgs";
import { getClientMode, requestApp, WEB_TO_APP } from "common/webview";
import { getStoredServiceId } from "common/app";
import { setAutoLogin } from "common/auth";
import { Device } from "common/device";


export const SignIn = () => {
    const { t } = useTranslation();
    const { navigate } = useQuery();
    const { onLogin } = userStore;
    const resetSignUp = useSetAtom(resetSignUpData);
    const [idVal, setIdVal] = useState("");
    const [pwdVal, setPwdVal] = useState("");
    const [autoLoginChecked, setAutoLoginChecked] = useState(() => {
        const stored = localStorage.getItem("isAutoLogin");
        return stored === null ? true : stored === "Y";
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const loginState = useAtomValue(userStore.login.stateAtom);

    // 로그인 상태 처리 훅
    useUser();

    // 로그인 상태 감지
    useEffect(() => {
        if (loginState.status === "pending") {
            setIsLoading(true);
            setErrorMsg("");
        } else if (loginState.status === "ready") {
            setIsLoading(false);
        } else if (loginState.status === "error") {
            setIsLoading(false);
            const error = loginState.error;
            // 에러 코드에 따른 메시지 처리
            if (error?.code === "INVALID_INPUT" || error?.code === "INVALID_CREDENTIALS" || error?.code === "AUTH_001") {
                setErrorMsg(t("signIn.error.invalidCredentials"));
            } else {
                setErrorMsg(t("signIn.error.default"));
            }
            console.error("❌ Login failed:", error);
        }
    }, [loginState.status, loginState.error, t]);

    const handleLogin = () => {
        // 유효성 검사
        if (!idVal.trim()) {
            setErrorMsg(t("signIn.error.emailRequired"));
            return;
        }
        if (!pwdVal.trim()) {
            setErrorMsg(t("signIn.error.passwordRequired"));
            return;
        }

        setErrorMsg("");

        // 자동 로그인 설정 저장 및 네이티브 전달
        setAutoLogin(autoLoginChecked);
        requestApp(WEB_TO_APP.AUTO_SIGN_IN, { autoLogin: autoLoginChecked });

        onLogin({
            serviceId: getStoredServiceId(),
            userId: idVal.trim(),
            password: pwdVal,
            os: Device.os || "etc",
            deviceId: Device.deviceId,
        });
    };

    return (
        <div className="sign-in-wrapper">
            <Header title={t("signInText")}/>
            <div style={{ display: "flex", flexDirection: "column", padding: "40px 20px" }}>
                <Input
                    inputVal={idVal}
                    setInputVal={setIdVal}
                    title={t("idText")}
                    type="email"
                    name="user_id"
                    placeholder={t("signIn.idPlaceHolder")}
                    disabled={isLoading}
                />
                <Input
                    inputVal={pwdVal}
                    setInputVal={setPwdVal}
                    title={t("passwordText")}
                    type="password"
                    name="password"
                    placeholder={t("signIn.pwdPlaceHolder")}
                    disabled={isLoading}
                    infoMsg={errorMsg ? { msg: errorMsg, type: "error" } : undefined}
                />
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                    {(window.__APP__?.hasAutoSignIn ?? true) ? (
                        <CheckBox
                            title={t("signIn.autoLogin")}
                            id="auto_login"
                            checked={autoLoginChecked}
                            setChecked={setAutoLoginChecked}
                        />
                    ) : <span />}
                    <span
                        className="signin-find-link"
                        onClick={() => navigate("/account-recovery")}
                    >
                        {t("signIn.findIdPwd")}
                    </span>
                </div>



                <Button
                    style={{ marginTop: "30px" }}
                    text={isLoading ? t("signIn.loading") : t("signInText")}
                    onPress={handleLogin}
                    disabled={isLoading}
                />
                <Button
                    style={{ marginTop: "10px" }}
                    buttonColor="var(--fixedBlack)"
                    text={t("signUpText")}
                    onPress={() => {
                        resetSignUp();
                        navigate('/sign-up');
                    }}
                />

                {getClientMode() === "native" && <div className="sign-in-social-container">
                    <span>{t('signIn.socialLogin')}</span>
                    <div className="sign-in-social-btn-wrapper">
                        
                        <button
                            className="sign-in-social-btn" title="social-sign-in-naver"
                            style={{backgroundColor: "#03A94D"}}
                            onClick={() => {requestApp(WEB_TO_APP.SOCIAL_SIGN_IN, {type: "NAVER"})}}
                        >
                            <Svgs.IcSocialNaver />
                        </button>

                        <button
                            className="sign-in-social-btn" title="social-sign-in-kakao"
                            style={{backgroundColor: "#fee500"}}
                            onClick={() => {requestApp(WEB_TO_APP.SOCIAL_SIGN_IN, {type: "KAKAO"})}}
                        >
                            <Svgs.IcSocialKakao />
                        </button>
                        
                        <button
                            className="sign-in-social-btn" title="social-sign-in-google"
                            style={{backgroundColor: "var(--fixedWhite)"}}
                            onClick={() => {requestApp(WEB_TO_APP.SOCIAL_SIGN_IN, {type: "GOOGLE"})}}
                        >
                            <Svgs.IcSocialGoogle />
                        </button>

                        <button
                            className="sign-in-social-btn" title="social-sign-in-apple"
                            style={{backgroundColor: "#000"}}
                            onClick={() => {requestApp(WEB_TO_APP.SOCIAL_SIGN_IN, {type: "APPLE"})}}
                        >
                            <Svgs.IcSocialApple style={{color: 'white'}}/>
                        </button>

                        <button
                            className="sign-in-social-btn" title="social-sign-in-line"
                            style={{backgroundColor: "#06C755"}}
                            onClick={() => {requestApp(WEB_TO_APP.SOCIAL_SIGN_IN, {type: "LINE"})}}
                        >
                            <Svgs.IcSocialLine />
                        </button>
                        
                    </div>
                </div>}
            </div>
        </div>
    );
};
